import { useRouter } from 'next/router'
import React from 'react'

import clsx from 'clsx'
import { UseFormReturn } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'

import { DeviceOrderData } from '@/lib/schemes'
import { Device, OrderEstimate } from '@/lib/types'
import { formatCurrency } from '@/lib/utils'

import { Discount } from '@/components/features/shop/OrderAmountBlock'
import ShopDiscountTag from '@/components/features/shop/ShopDiscountTag'
import RowSkeleton from '@/components/skeletons/RowSkeleton'

interface TotalData {
  name: string
  count?: JSX.Element
  amount: number | undefined | JSX.Element
}

export const useOrderTotalInfo = ({
  form: { watch },
  device,
  estimate,
  isLoading
}: {
  form: UseFormReturn<DeviceOrderData>
  device: Device
  estimate?: OrderEstimate
  isLoading: boolean
}) => {
  const { locale } = useRouter()
  const { formatMessage } = useIntl()

  const {
    bonusDiscount,
    canPayWithBonuses,
    hardwarePrice,
    discount,
    discountPercentage,
    installmentTerms,
    cryptoDiscount,
    cryptoDiscountPercentage
  } = estimate || {}

  const { downPayment, downPaymentPercentage } = installmentTerms || {}
  const { type, currency } = device

  const isFullMiner = type === 'full'
  const useBonusBalance = watch('useBonusBalance')
  const isPaymentWithCrypto = watch('paymentMethod') === 'usdt_trc20'

  const initialFee = {
    name: (
      <FormattedMessage
        {...{
          id: 'dialog.order.installment.down',
          defaultMessage: 'Первоначальный взнос {downPaymentPercentage}%',
          values: { downPaymentPercentage }
        }}
      />
    ),
    amount: downPayment
  }

  const orderAmount = {
    name: device.miner.name,
    count: (
      <div className="w-full text-center text-secondary-500">
        {watch('orderAmount')}{' '}
        {isFullMiner
          ? formatMessage({ id: 'pieces', defaultMessage: 'шт' })
          : 'TH'}
      </div>
    ),
    amount: isLoading ? <RowSkeleton className="h-6 w-20" /> : hardwarePrice
  }

  const discountAmount = {
    name: formatMessage({ id: 'dialog.discount', defaultMessage: 'Скидка' }),
    count: (
      <ShopDiscountTag>
        <Discount {...{ discountPercentage }} />
      </ShopDiscountTag>
    ),
    amount: `- ${discount && formatCurrency(discount, currency, locale)}`
  }

  const paymentWithBonuses = {
    name: formatMessage({
      id: 'dialog.order.payment.bonus',
      defaultMessage: 'Оплата бонусами'
    }),
    amount: `- ${
      bonusDiscount && formatCurrency(bonusDiscount, currency, locale)
    }`
  }

  const hostingPrepayment = {
    name: formatMessage({
      id: 'dialog.order.hosting',
      defaultMessage: 'Предоплата за хостинг'
    }),
    amount: (watch('advance') || 0) * 100
  }

  const paymentWithTRC = {
    name: formatMessage({
      id: 'dialog.order.payment.usdt',
      defaultMessage: 'Скидка при оплате USDT'
    }),
    count: (
      <span
        className={clsx(
          'inline-flex h-5 items-center justify-center rounded-md bg-green-100',
          'px-2 py-1 text-[11px] font-medium leading-x-sm text-green-600'
        )}
      >
        <Discount {...{ discountPercentage: cryptoDiscountPercentage }} />
      </span>
    ),
    amount: isLoading ? (
      <RowSkeleton className="h-6 w-20" />
    ) : (
      `- ${formatCurrency(cryptoDiscount || 0, currency, locale)}`
    )
  }

  const paymentWithBonusesInfo =
    canPayWithBonuses && useBonusBalance && paymentWithBonuses

  const hostingPrepaymentInfo =
    watch('paymentMethod') !== 'balance' && hostingPrepayment

  const totalOrderInfo = [
    orderAmount,
    discountPercentage && discountAmount,
    installmentTerms && initialFee,
    hostingPrepaymentInfo,
    paymentWithBonusesInfo,
    isPaymentWithCrypto && paymentWithTRC
  ].filter(Boolean) as TotalData[]

  return { totalOrderInfo }
}
